export class AuthError extends Error {
  constructor(message, status = 401) {
    super(message);
    this.status = status;
  }
}

const INTERNAL_CONFIG_KEY = 'internalApiConfig';
const EXTERNAL_SERVERS_KEY = 'externalServers';
const TOKEN_CACHE_KEY = 'authTokenCache';

const AUTH_TYPES = {
  JWT: 'jwt',
  CLIENT_CREDENTIALS: 'client_credentials'
};

const normalizeBaseUrl = (value) => {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
};

const normalizeAuthType = (value, fallback = AUTH_TYPES.JWT) => {
  const normalized = (value || '').trim().toLowerCase();
  if (normalized === AUTH_TYPES.CLIENT_CREDENTIALS) {
    return AUTH_TYPES.CLIENT_CREDENTIALS;
  }
  if (normalized === AUTH_TYPES.JWT) {
    return AUTH_TYPES.JWT;
  }
  return fallback;
};

const detectLegacyAuthType = (config = {}) => {
  if (config.authType) {
    return normalizeAuthType(config.authType);
  }
  if (config.tokenUrl || config.clientId || config.clientSecret) {
    return AUTH_TYPES.CLIENT_CREDENTIALS;
  }
  return AUTH_TYPES.JWT;
};

const decodeBase64 = (value) => {
  if (typeof atob === 'function') {
    return atob(value);
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(value, 'base64').toString('utf-8');
  }
  throw new Error('No base64 decoder available');
};

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }

  try {
    const payload = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const paddedPayload = payload.padEnd(payload.length + (4 - (payload.length % 4 || 4)) % 4, '=');
    return JSON.parse(decodeBase64(paddedPayload));
  } catch {
    return null;
  }
};

const getJwtExpiry = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return null;
  }
  return payload.exp * 1000;
};

export const isJwtExpired = (token, skewMs = 30_000) => {
  const expiry = getJwtExpiry(token);
  if (!expiry) {
    return false;
  }
  return Date.now() >= (expiry - skewMs);
};

const parseJson = (value, fallback) => {
  if (!value) {
    return fallback;
  }
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export const normalizeServerConfig = (config = {}) => {
  const authType = detectLegacyAuthType(config);
  return {
    id: (config.id || '').toString(),
    name: (config.name || '').trim(),
    baseUrl: normalizeBaseUrl(config.baseUrl),
    authType,
    token: (config.token || '').trim(),
    tokenUrl: (config.tokenUrl || '').trim(),
    clientId: (config.clientId || '').trim(),
    clientSecret: (config.clientSecret || '').trim()
  };
};

export const getInternalServerConfig = () => {
  const storedConfig = parseJson(localStorage.getItem(INTERNAL_CONFIG_KEY), null);

  if (storedConfig) {
    return {
      ...normalizeServerConfig(storedConfig),
      id: '__internal__',
      name: 'Internal API'
    };
  }

  return normalizeServerConfig({
    id: '__internal__',
    name: 'Internal API',
    baseUrl: localStorage.getItem('baseUrl') || '',
    token: localStorage.getItem('token') || ''
  });
};

export const getExternalServers = () => {
  const stored = parseJson(localStorage.getItem(EXTERNAL_SERVERS_KEY), []);
  if (!Array.isArray(stored)) {
    return [];
  }
  return stored.map((server) => normalizeServerConfig(server));
};

export const validateServerConfig = (serverConfig, label = 'Server') => {
  const config = normalizeServerConfig(serverConfig);
  const errors = [];

  if (!config.baseUrl) {
    errors.push(`${label} base URL is not configured`);
  } else {
    try {
      new URL(config.baseUrl);
    } catch {
      errors.push(`${label} base URL format is invalid`);
    }
  }

  if (config.authType === AUTH_TYPES.JWT) {
    if (!config.token) {
      errors.push(`${label} JWT token is not configured`);
    }
  } else {
    if (!config.tokenUrl) {
      errors.push(`${label} token URL is not configured`);
    } else {
      try {
        new URL(config.tokenUrl);
      } catch {
        errors.push(`${label} token URL format is invalid`);
      }
    }

    if (!config.clientId) {
      errors.push(`${label} client ID is not configured`);
    }

    if (!config.clientSecret) {
      errors.push(`${label} client secret is not configured`);
    }
  }

  return errors;
};

const getTokenCache = () => parseJson(localStorage.getItem(TOKEN_CACHE_KEY), {});

const setTokenCache = (cache) => {
  localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(cache));
};

const getTokenCacheKey = (serverConfig) => {
  const config = normalizeServerConfig(serverConfig);
  if (config.id) {
    return config.id;
  }

  return [
    config.baseUrl,
    config.authType,
    config.tokenUrl,
    config.clientId
  ].join('|');
};

const isCachedTokenExpired = (cachedToken) => {
  if (!cachedToken?.token) {
    return true;
  }

  if (cachedToken.expiresAt && Date.now() >= (cachedToken.expiresAt - 30_000)) {
    return true;
  }

  return isJwtExpired(cachedToken.token);
};

const getCachedToken = (serverConfig) => {
  const cache = getTokenCache();
  const key = getTokenCacheKey(serverConfig);
  const cachedToken = cache[key];
  if (!cachedToken) {
    return null;
  }

  if (isCachedTokenExpired(cachedToken)) {
    delete cache[key];
    setTokenCache(cache);
    return null;
  }

  return cachedToken.token;
};

const setCachedToken = (serverConfig, token, expiresAt) => {
  const cache = getTokenCache();
  const key = getTokenCacheKey(serverConfig);
  cache[key] = {
    token,
    expiresAt
  };
  setTokenCache(cache);
};

export const clearCachedTokenForServer = (serverConfig) => {
  const cache = getTokenCache();
  const key = getTokenCacheKey(serverConfig);
  if (cache[key]) {
    delete cache[key];
    setTokenCache(cache);
  }
};

export const clearAllCachedTokens = () => {
  localStorage.removeItem(TOKEN_CACHE_KEY);
};

const computeTokenExpiry = (accessToken, expiresIn) => {
  const jwtExpiry = getJwtExpiry(accessToken);
  const expiresInMs = typeof expiresIn === 'number' && expiresIn > 0
    ? Date.now() + Math.max(expiresIn - 30, 0) * 1000
    : null;

  if (jwtExpiry && expiresInMs) {
    return Math.min(jwtExpiry, expiresInMs);
  }

  if (jwtExpiry) {
    return jwtExpiry;
  }

  if (expiresInMs) {
    return expiresInMs;
  }

  return Date.now() + 5 * 60 * 1000;
};

const requestClientCredentialsToken = async (serverConfig) => {
  const config = normalizeServerConfig(serverConfig);

  const response = await fetch(config.tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: new URLSearchParams({
      grant_type: 'client_credentials',
      client_id: config.clientId,
      client_secret: config.clientSecret
    }).toString()
  });

  if (!response.ok) {
    const body = await response.text();
    throw new AuthError(`Failed to obtain access token: ${response.status} ${body}`.trim(), response.status);
  }

  const tokenResponse = await response.json();
  if (!tokenResponse?.access_token) {
    throw new AuthError('Token endpoint did not return an access token', 401);
  }

  const expiry = computeTokenExpiry(tokenResponse.access_token, tokenResponse.expires_in);
  setCachedToken(config, tokenResponse.access_token, expiry);

  return tokenResponse.access_token;
};

export const getAuthToken = async (serverConfig) => {
  const config = normalizeServerConfig(serverConfig);
  const errors = validateServerConfig(config);

  if (errors.length > 0) {
    throw new AuthError(errors.join('. '), 401);
  }

  if (config.authType === AUTH_TYPES.JWT) {
    if (isJwtExpired(config.token)) {
      throw new AuthError('Configured JWT token is expired. Please update it in settings.', 401);
    }
    return config.token;
  }

  const cachedToken = getCachedToken(config);
  if (cachedToken) {
    return cachedToken;
  }

  return requestClientCredentialsToken(config);
};

export const getServerConfigByBaseUrl = (baseUrl) => {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  if (!normalizedBaseUrl) {
    return null;
  }

  const internalServer = getInternalServerConfig();
  if (internalServer.baseUrl === normalizedBaseUrl) {
    return internalServer;
  }

  const externalServer = getExternalServers().find((server) => server.baseUrl === normalizedBaseUrl);
  return externalServer || null;
};

export const resolveServerConfig = (serverDetails = {}) => {
  const normalizedBaseUrl = normalizeBaseUrl(serverDetails.baseUrl || '');
  const storedConfig = normalizedBaseUrl ? getServerConfigByBaseUrl(normalizedBaseUrl) : null;

  if (!storedConfig) {
    return normalizeServerConfig(serverDetails);
  }

  const resolved = { ...storedConfig };

  if (typeof serverDetails.authType === 'string' && serverDetails.authType.trim()) {
    resolved.authType = serverDetails.authType.trim();
  }

  ['id', 'name', 'token', 'tokenUrl', 'clientId', 'clientSecret'].forEach((key) => {
    if (typeof serverDetails[key] === 'string' && serverDetails[key].trim()) {
      resolved[key] = serverDetails[key].trim();
    }
  });

  return normalizeServerConfig(resolved);
};

export const getAuthHeaders = async (serverDetails = {}, extraHeaders = {}) => {
  const resolvedConfig = resolveServerConfig(serverDetails);
  const token = await getAuthToken(resolvedConfig);

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`
  };
};

export const getAuthHeadersForBaseUrl = async (baseUrl, extraHeaders = {}) => {
  const config = getServerConfigByBaseUrl(baseUrl);
  if (!config) {
    throw new AuthError(`No server configuration found for ${baseUrl}`, 404);
  }

  const token = await getAuthToken(config);

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`
  };
};

export const authTypes = AUTH_TYPES;
